import { useEffect, useMemo } from 'react';
import { useFieldArray, useWatch, type UseFormReturn } from 'react-hook-form';

import { type ExamCourse } from '../../constants/types';
import {
  type PreExamSettingsSectionFormInputs,
  type PreExamSettingsSectionFormOutputs,
} from '../../containers/pre-exam-settings-section-form/types';

import { checkAddSessionCondition, mapExamCourseSettingsToPreExamSettingsFormData } from './utils';

type ExamDuration = ExamCourse['settings']['examDuration'];

export default function usePreExamSettingsFormLogic(
  form: UseFormReturn<PreExamSettingsSectionFormInputs, unknown, PreExamSettingsSectionFormOutputs>,
  examDuration: ExamDuration | null | undefined,
  minSession: number,
  maxSession: number,
) {
  const { control, trigger, watch, formState, getFieldState, reset } = form;

  const [examDurationType, multipleSessionValue] = useWatch({
    control,
    name: ['examDuration.type', 'examDuration.multipleSessionValue'],
  });

  const { fields, append, remove } = useFieldArray({
    name: 'examDuration.multipleSessionValue',
    control,
  });

  useEffect(() => {
    const formData = mapExamCourseSettingsToPreExamSettingsFormData(examDuration);
    reset(formData);
  }, [examDuration, reset]);

  const multipleSessions = watch('examDuration.multipleSessionValue') ?? [];

  const visibleFields = useMemo(() => {
    return {
      singleSessionDuration: examDurationType === 'single-session',
      multipleSessionDuration: examDurationType === 'multiple-session',
    };
  }, [examDurationType]);

  useEffect(() => {
    if (multipleSessionValue && formState.isSubmitted) {
      trigger('examDuration.multipleSessionValue');
    }
  }, [multipleSessionValue, formState.isSubmitted, trigger]);

  useEffect(() => {
    if (examDurationType) {
      trigger('examDuration.type');
    }
  }, [examDurationType, trigger]);

  const handleAddSession = () => {
    append({ value: undefined });
  };

  const handleRemoveSession = (index: number) => {
    remove(index);
  };

  const canAddNewSession = checkAddSessionCondition(multipleSessions, minSession, maxSession);

  const totalSessionTime = multipleSessions.reduce((acc, field) => {
    const value = field.value;
    if (value === undefined) {
      return acc;
    }
    return acc + value;
  }, 0);

  const { error } = getFieldState('examDuration.type', formState);
  const { error: multipleSessionsError } = getFieldState(
    'examDuration.multipleSessionValue',
    formState,
  );

  return {
    visibleFields,
    fields,
    handleAddSession,
    handleRemoveSession,
    totalSessionTime,
    canAddNewSession,
    multipleSessions,
    errors: {
      examDurationType: error,
      multipleSessions: multipleSessionsError,
    },
  };
}
